import React, { useCallback, useEffect, useRef, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { getLocation } from "../../../graphql/queries";
import { getOutboundShipment } from "../../../graphql/outbound/custom-queries";
import { trackCarByActiveByTrackBySortKeyMini } from "../../../graphql/trackCar/trackCar-queries";
import { locationByAreaBySortKey } from "../../../graphql/inventory/custom-queries";

import {
  Button,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
} from "@material-ui/core";
import SaveAltOutlinedIcon from "@material-ui/icons/SaveAltOutlined";

import { useNotify } from "react-admin";

import {
  AclType,
  AreaType,
  CarrierType,
  InventoryStatus,
  LocationType,
  originDamageList,
} from "../../../utils/constants";
import { isAuthorized, logError } from "../../../utils/appUtils";
import {
  getLocationFullName,
  isTruckToRail,
} from "../../../utils/locationUtils";
import { normalizeRailCarNumber } from "../../../utils/railCarUtils";
import { useTrackCarSubscriptionRefresh } from "../../../utils/useTrackCarSubscriptionRefresh";
import { useWorkOrderContext } from "./context";

/**
 * `blockedBy` values returned by getRailcarVerificationSaveState for inbound track save only.
 * Outbound crew uses different literals for the same idea: `trackRailcarVerificationType` is
 * "supplying" | "receiving" (see outboundShipments/utils.js getTrackRailcarVerificationConfig).
 * These enums are not interchangeable — compare blockedBy only against the constants below.
 */
const TRACK_SAVE_BLOCKED_BY_SOURCE_RAILCAR = "source";
const TRACK_SAVE_BLOCKED_BY_TARGET_RAILCAR = "target";

const projectTrackCarToOutboundOption = (trackCar) => {
  if (!trackCar || !trackCar.outbound) return null;
  const outbound = trackCar.outbound;
  return {
    id: outbound.id,
    railCarNumber: trackCar.railCarNumber,
    carrierType: outbound.carrierType,
    accountName: outbound.accountName,
    account: outbound.account,
    shipmentLineItems: outbound.shipmentLineItems,
    trackCar: { id: trackCar.id, trackId: trackCar.trackId },
  };
};

export const projectOutboundShipmentToOutboundOption = (outboundShipment) => {
  if (!outboundShipment?.id) return null;

  return {
    id: outboundShipment.id,
    railCarNumber: outboundShipment.railCarNumber,
    carrierType: outboundShipment.carrierType,
    accountName: outboundShipment.accountName,
    account: outboundShipment.account,
    shipmentLineItems: outboundShipment.shipmentLineItems,
    trackCar: outboundShipment.trackCar
      ? {
          id: outboundShipment.trackCar.id,
          trackId: outboundShipment.trackCar.trackId,
        }
      : undefined,
  };
};

const TRACK_CAR_BATCH_SIZE = 5;
const TRACK_CAR_QUERY_LIMIT = 200;
const TRACK_LOCATION_CACHE_ALL_KEY = "ALL_TRACKS";

const getTrackAreaIds = (targetAreaId = null) => {
  const areas = window.appUser?.reloadYard?.areas?.items || [];
  if (targetAreaId) {
    const targetArea = areas.find((area) => area.id === targetAreaId);
    if (!targetArea || targetArea.areaType === AreaType.TRACK) {
      return [targetAreaId];
    }
    return [];
  }
  return areas
    .filter((area) => area.areaType === AreaType.TRACK)
    .map((area) => area.id);
};

const extractTrackLocationIdsFromAppUser = (areaIds) => {
  const reloadYard = window.appUser?.reloadYard;
  if (!reloadYard || !areaIds?.length) return [];
  const areaIdSet = new Set(areaIds);
  const ids = new Set();

  const addIfMatch = (location) => {
    if (!location?.id) return;
    if (location.type !== LocationType.TRACK) return;
    if (areaIdSet.size && !areaIdSet.has(location.areaId)) return;
    ids.add(location.id);
  };

  const directLocations = reloadYard.locations?.items;
  if (Array.isArray(directLocations) && directLocations.length) {
    directLocations.forEach(addIfMatch);
    if (ids.size) return Array.from(ids);
  }

  const mapValues = reloadYard.locationMap
    ? Object.values(reloadYard.locationMap)
    : [];
  if (mapValues.length) {
    mapValues.forEach(addIfMatch);
    if (ids.size) return Array.from(ids);
  }

  const aisles = reloadYard.aisles?.items || [];
  aisles.forEach((aisle) => {
    if (areaIdSet.size && !areaIdSet.has(aisle.areaId)) return;
    (aisle.locations?.items || []).forEach(addIfMatch);
  });

  return Array.from(ids);
};

const fetchTrackLocationIdsFromApi = async (areaIds, reloadYardIdValue) => {
  if (!reloadYardIdValue || !areaIds?.length) return [];
  const ids = new Set();

  for (const areaId of areaIds) {
    let nextToken = null;
    do {
      const response = await API.graphql(
        graphqlOperation(locationByAreaBySortKey, {
          reloadYardId: reloadYardIdValue,
          areaIdSortKey: { beginsWith: { areaId } },
          limit: 500,
          nextToken,
        }),
      );
      const page = response?.data?.locationByAreaBySortKey;
      page?.items?.forEach((location) => {
        if (
          location?.type === LocationType.TRACK &&
          (!areaId || location.areaId === areaId)
        ) {
          ids.add(location.id);
        }
      });
      nextToken = page?.nextToken || null;
    } while (nextToken);
  }

  return Array.from(ids);
};

const resolveTrackLocationIds = async ({
  areaId = null,
  reloadYardId,
  cacheRef,
  promiseRef,
  notify,
}) => {
  const cacheKey = areaId || TRACK_LOCATION_CACHE_ALL_KEY;
  if (cacheRef.current[cacheKey]) {
    return cacheRef.current[cacheKey];
  }
  if (promiseRef.current[cacheKey]) {
    // Explicitly await the in-flight promise to make intent clear
    // (functionally equivalent, but clearer that we're deduplicating concurrent requests)
    return await promiseRef.current[cacheKey];
  }

  const areaIds = getTrackAreaIds(areaId);
  if (!areaIds.length) return [];

  const cachedIds = extractTrackLocationIdsFromAppUser(areaIds);
  if (cachedIds.length) {
    cacheRef.current[cacheKey] = cachedIds;
    return cachedIds;
  }

  const fetchPromise = (async () => {
    try {
      return await fetchTrackLocationIdsFromApi(areaIds, reloadYardId);
    } catch (err) {
      logError(
        "processWorkOrders:TrackAisleControl:fetchTrackLocations",
        err,
        notify,
      );
      return [];
    }
  })();

  promiseRef.current[cacheKey] = fetchPromise;
  const ids = await fetchPromise;
  cacheRef.current[cacheKey] = ids;
  promiseRef.current[cacheKey] = null;
  return ids;
};

const fetchTrackCarsForTrack = async ({ trackId, reloadYardId }) => {
  const items = [];
  if (!trackId) return items;
  let nextToken = null;

  do {
    const response = await API.graphql(
      graphqlOperation(trackCarByActiveByTrackBySortKeyMini, {
        reloadYardId,
        activeTrackIdSortKey: { beginsWith: { active: "true", trackId } },
        limit: TRACK_CAR_QUERY_LIMIT,
        nextToken,
      }),
    );
    const page = response?.data?.trackCarByActiveByTrackBySortKey;
    if (page?.items?.length) {
      items.push(...page.items);
    }
    nextToken = page?.nextToken || null;
  } while (nextToken);

  return items;
};

const fetchTrackCarsAcrossTracks = async ({
  trackIds,
  reloadYardId,
  notify,
}) => {
  if (!trackIds?.length) return [];
  const allCars = [];

  for (let i = 0; i < trackIds.length; i += TRACK_CAR_BATCH_SIZE) {
    const chunk = trackIds.slice(i, i + TRACK_CAR_BATCH_SIZE);
    const chunkResults = await Promise.all(
      chunk.map((trackId) =>
        fetchTrackCarsForTrack({ trackId, reloadYardId }).catch((err) => {
          logError(
            "processWorkOrders:TrackAisleControl:fetchTrackCars",
            err,
            notify,
          );
          return [];
        }),
      ),
    );
    chunkResults.forEach((cars) => {
      if (cars?.length) {
        allCars.push(...cars);
      }
    });
  }

  return allCars;
};

export const buildOutboundOptions = (trackCars = []) => {
  const seen = new Set();
  return trackCars
    .map((trackCar) => projectTrackCarToOutboundOption(trackCar))
    .filter((outbound) => {
      if (!outbound?.id) return false;
      if (seen.has(outbound.id)) return false;
      seen.add(outbound.id);
      return true;
    });
};

export const resolveSelectedOutboundState = ({
  outboundOptions = [],
  selectedOutboundId,
  hydratedSelectedOutbound,
}) => {
  if (!selectedOutboundId) {
    return {
      outboundOptions,
      selectedOutbound: null,
    };
  }

  const selectedOutbound =
    hydratedSelectedOutbound ||
    outboundOptions.find((outbound) => outbound.id === selectedOutboundId) ||
    null;

  if (!selectedOutbound?.id) {
    return {
      outboundOptions,
      selectedOutbound: null,
    };
  }

  const nextOutboundOptions = outboundOptions.some(
    (outbound) => outbound.id === selectedOutbound.id,
  )
    ? outboundOptions.map((outbound) =>
        outbound.id === selectedOutbound.id ? selectedOutbound : outbound,
      )
    : [...outboundOptions, selectedOutbound];

  return {
    outboundOptions: nextOutboundOptions,
    selectedOutbound,
  };
};

export const getRailcarVerificationSaveState = ({
  requiresSourceRailcarVerification = false,
  isSourceRailcarValidated = false,
  requiresTargetRailcarVerification = false,
  isTargetRailcarValidated = false,
}) => {
  if (requiresSourceRailcarVerification && !isSourceRailcarValidated) {
    return {
      isBlocked: true,
      blockedBy: TRACK_SAVE_BLOCKED_BY_SOURCE_RAILCAR,
    };
  }

  if (requiresTargetRailcarVerification && !isTargetRailcarValidated) {
    return {
      isBlocked: true,
      blockedBy: TRACK_SAVE_BLOCKED_BY_TARGET_RAILCAR,
    };
  }

  return {
    isBlocked: false,
    blockedBy: null,
  };
};

export const getTrackSaveBlockedMessage = (blockedBy) => {
  if (blockedBy === TRACK_SAVE_BLOCKED_BY_SOURCE_RAILCAR) {
    return "Validate the supplying railcar before saving.";
  }

  if (blockedBy === TRACK_SAVE_BLOCKED_BY_TARGET_RAILCAR) {
    return "Validate the receiving railcar before saving.";
  }

  return "";
};

export const isTrackSaveDisabled = ({
  quantityOnHand,
  status,
  isAuthorizedToAssignLocation,
  requiresSourceRailcarVerification = false,
  isSourceRailcarValidated = false,
  requiresTargetRailcarVerification = false,
  isTargetRailcarValidated = false,
}) => {
  const railcarVerificationSaveState = getRailcarVerificationSaveState({
    requiresSourceRailcarVerification,
    isSourceRailcarValidated,
    requiresTargetRailcarVerification,
    isTargetRailcarValidated,
  });

  if (!quantityOnHand) return true;
  if (!isAuthorizedToAssignLocation && status !== InventoryStatus.IN_TRANSIT) {
    return true;
  }
  if (railcarVerificationSaveState.isBlocked) {
    return true;
  }

  return false;
};

export const getTargetRailcarVerificationState = ({
  selectedInventoryItem,
  selectedOutbound,
  isTargetRailcarValidated,
}) => {
  const expectedRailCarNumber = selectedOutbound?.railCarNumber || "";
  const hasExpectedRailCarNumber = Boolean(
    normalizeRailCarNumber(expectedRailCarNumber),
  );
  const isAssociatedToOutbound = Boolean(
    selectedInventoryItem?.outboundShipmentId,
  );
  const requiresVerification =
    isAssociatedToOutbound &&
    selectedOutbound?.carrierType === CarrierType.RAIL &&
    selectedOutbound?.account?.verifyOutboundRailCar === true &&
    hasExpectedRailCarNumber;

  return {
    expectedRailCarNumber,
    requiresVerification,
    shouldHideSelectedRailcar:
      requiresVerification && !isTargetRailcarValidated,
  };
};

export const TrackAisleControl = ({
  handleSave,
  classes,
  requiresSourceRailcarVerification = false,
  isSourceRailcarValidated = false,
  requiresTargetRailcarVerification = false,
  isTargetRailcarValidated = false,
}) => {
  const { state, dispatch } = useWorkOrderContext();
  const {
    currentInventory,
    selectedInventoryItem,
    selectedOutbound,
    currentShipment,
    disableSelection,
  } = state;
  const trackAreaId = currentInventory?.areaId;
  const selectedOutboundId = selectedInventoryItem?.outboundShipmentId;
  const [outbounds, setOutbounds] = useState([]);
  const notify = useNotify();
  const trackLocationCacheRef = useRef({});
  const trackLocationPromiseRef = useRef({});

  const getSelectedOutboundOption = useCallback(async () => {
    if (!selectedOutboundId) return null;

    try {
      const response = await API.graphql(
        graphqlOperation(getOutboundShipment, { id: selectedOutboundId }),
      );

      return projectOutboundShipmentToOutboundOption(
        response?.data?.getOutboundShipment,
      );
    } catch (err) {
      logError(
        "processWorkOrders:TrackAisleControl:getSelectedOutboundOption",
        err,
        notify,
      );
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOutboundId]);

  // Initialize selected inventory item on mount only
  // Using empty deps to avoid infinite loop - selectedInventoryItem comes from context
  useEffect(() => {
    dispatch({
      type: "SET_SELECTED_INVENTORY_ITEM",
      payload: selectedInventoryItem,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getOutboundData = useCallback(async () => {
    try {
      const reloadYardId = window.appUser?.reloadYardId;
      if (!reloadYardId) {
        setOutbounds([]);
        notify(
          "Track Management requires a reload yard selection. Please select a yard and retry.",
          "warning",
        );
        return;
      }
      const trackIds = await resolveTrackLocationIds({
        areaId: trackAreaId,
        reloadYardId,
        cacheRef: trackLocationCacheRef,
        promiseRef: trackLocationPromiseRef,
        notify,
      });
      if (!trackIds.length) {
        const resolvedSelectedOutboundState = resolveSelectedOutboundState({
          outboundOptions: [],
          selectedOutboundId,
          hydratedSelectedOutbound: await getSelectedOutboundOption(),
        });
        setOutbounds(resolvedSelectedOutboundState.outboundOptions);
        dispatch({
          type: "SET_SELECTED_OUTBOUND",
          payload: resolvedSelectedOutboundState.selectedOutbound,
        });
        return;
      }

      const trackCars = await fetchTrackCarsAcrossTracks({
        trackIds,
        reloadYardId,
        notify,
      });

      let outboundOptions = buildOutboundOptions(trackCars);

      if (selectedOutboundId) {
        const resolvedSelectedOutboundState = resolveSelectedOutboundState({
          outboundOptions,
          selectedOutboundId,
          hydratedSelectedOutbound: await getSelectedOutboundOption(),
        });
        outboundOptions = resolvedSelectedOutboundState.outboundOptions;
        dispatch({
          type: "SET_SELECTED_OUTBOUND",
          payload: resolvedSelectedOutboundState.selectedOutbound,
        });
      }

      setOutbounds(outboundOptions);
    } catch (err) {
      logError(
        "processWorkOrders:TrackAisleControl:getOutbounds",
        err,
        notify,
      );
    }
  }, [
    dispatch,
    getSelectedOutboundOption,
    notify,
    selectedOutboundId,
    trackAreaId,
  ]);

  useEffect(() => {
    if (isTruckToRail(trackAreaId, currentShipment)) {
      getOutboundData();
    }
  }, [currentShipment, getOutboundData, trackAreaId]);

  const reloadYardId = window.appUser?.reloadYardId;

  useTrackCarSubscriptionRefresh({
    enabled:
      isTruckToRail(trackAreaId, currentShipment) && Boolean(reloadYardId),
    label: "TrackAisleControl",
    shouldRefresh: (trackCar) => trackCar?.reloadYardId === reloadYardId,
    onRefresh: () => {
      getOutboundData();
    },
  });

  const setQuantity = (value) => {
    if (!value) value = 0;
    const quantity = Number(value);
    selectedInventoryItem.quantityOnHand = quantity;

    // const newInventoryItem = {...selectedInventoryItem, quantityOnHand: quantity};
    dispatch({
      type: "SET_SELECTED_INVENTORY_ITEM",
      payload: selectedInventoryItem,
    });
  };

  const doHandleSave = async () => {
    const railcarVerificationSaveState = getRailcarVerificationSaveState({
      requiresSourceRailcarVerification,
      isSourceRailcarValidated,
      requiresTargetRailcarVerification,
      isTargetRailcarValidated,
    });

    if (railcarVerificationSaveState.isBlocked) {
      notify(
        getTrackSaveBlockedMessage(railcarVerificationSaveState.blockedBy),
        "warning",
      );
      return;
    }

    await handleSave();
    // handleClose();
  };

  const handleKeyDown = (event) => {
    const key = event.key;
    const keyCode = event.keyCode || event.which; // Handle different browser behaviors
    // Allow digits, backspace (8), and delete (46)
    if (!/^\d$/.test(key) && keyCode !== 8 && keyCode !== 46) {
      event.preventDefault();
      return;
    }
  };

  const getQuantity = () => {
    return selectedInventoryItem?.quantityOnHand
      ? selectedInventoryItem.quantityOnHand
      : "";
  };

  const isSaveButtonDisabled = () => {
    return isTrackSaveDisabled({
      quantityOnHand: selectedInventoryItem?.quantityOnHand,
      status: selectedInventoryItem?.status,
      isAuthorizedToAssignLocation: isAuthorized(AclType.ASSIGN_LOCATION),
      requiresSourceRailcarVerification,
      isSourceRailcarValidated,
      requiresTargetRailcarVerification,
      isTargetRailcarValidated,
    });
  };

  const isQuantityDisabled = () => {
    if (!isAuthorized(AclType.ASSIGN_LOCATION)) {
      return true;
    }
    return false;
  };

  const setIsDamaged = (value) => {
    const inventoryItem = selectedInventoryItem
      ? { ...selectedInventoryItem }
      : {};
    if (value) {
      inventoryItem.isDamaged = true;
      inventoryItem.originOfDamage = value;
      inventoryItem.hasException = true;
    } else {
      inventoryItem.isDamaged = false;
      inventoryItem.originOfDamage = "";
      inventoryItem.hasException = false;
    }
    dispatch({ type: "SET_SELECTED_INVENTORY_ITEM", payload: inventoryItem });
  };

  const handleOutboundChange = async (outboundShipmentId) => {
    const outbound = outbounds.find(
      (outboundShipment) => outboundShipment.id === outboundShipmentId,
    );
    dispatch({ type: "SET_SELECTED_OUTBOUND", payload: outbound });
    const location = await getOutboundLocation(outbound);
    if (!location?.id) return;
    const newInventoryItem = { ...selectedInventoryItem, outboundShipmentId };
    newInventoryItem.inventoryId = currentInventory.id;
    newInventoryItem.areaId = location.areaId;
    newInventoryItem.zoneId = location.zoneId;
    newInventoryItem.aisleId = location.aisleId;
    newInventoryItem.locationId = location.id;
    newInventoryItem.location = location;
    newInventoryItem.locationFullName = getLocationFullName(location);
    dispatch({
      type: "SET_SELECTED_INVENTORY_ITEM",
      payload: newInventoryItem,
    });
  };

  const getOutboundLocation = async (outboundShipment) => {
    try {
      const outboundTrackId = outboundShipment?.trackCar?.trackId;
      if (!outboundTrackId) {
        logError(
          "processWorkOrders:TrackAisleControl:getOutboundLocation:missingTrackCarTrackId",
          new Error("Missing outbound trackCar.trackId"),
          notify,
        );
        return null;
      }
      const response = await API.graphql(
        graphqlOperation(getLocation, { id: outboundTrackId }),
      );
      const location = response.data.getLocation;
      return location;
    } catch (err) {
      logError(
        "processWorkOrders:TrackAisleControl:getOutboundLocation",
        err,
        notify,
      );
    }
  };

  return (
    <Grid item container md={12} lg={9} xl={6} spacing={2}>
      {isTruckToRail(currentInventory.areaId, currentShipment) && (
        <Grid item xs={12} sm={3}>
          <TextField
            select
            id="outbound"
            label="Outbound"
            variant="outlined"
            size="small"
            disabled={disableSelection}
            helperText={selectedOutbound?.accountName}
            required
            fullWidth
            value={
              selectedInventoryItem?.outboundShipmentId
                ? selectedInventoryItem.outboundShipmentId
                : ""
            }
            onChange={(evt) => handleOutboundChange(evt.target.value)}
          >
            {outbounds &&
              [
                { id: "", value: "", name: <span>&nbsp;</span> },
                ...outbounds,
              ].map((outboundRow) => (
                <MenuItem key={outboundRow.id} value={outboundRow.id}>
                  {outboundRow.railCarNumber}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
      )}
      <Grid item xs={12} sm={2}>
        <TextField
          id="track"
          label="Track"
          variant="outlined"
          size="small"
          disabled
          fullWidth
          value={
            selectedInventoryItem?.locationFullName
              ? selectedInventoryItem.locationFullName
              : ""
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          id="quantity-received"
          label="Quantity"
          variant="outlined"
          size="small"
          type="number"
          fullWidth
          inputProps={{ min: 0 }}
          disabled={isQuantityDisabled()}
          value={getQuantity()}
          error={!getQuantity()}
          onChange={(evt) => setQuantity(evt.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {currentInventory.inventoryUOM}
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <TextField
          select
          id="damage"
          label="Damaged"
          variant="outlined"
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={
            selectedInventoryItem?.originOfDamage
              ? selectedInventoryItem.originOfDamage
              : ""
          }
          onChange={(evt) => setIsDamaged(evt.target.value)}
          SelectProps={{
            displayEmpty: true,
            renderValue: (value) => {
              if (!value) return "No Damage";
              const matchedDamage = originDamageList.find(
                (item) => item.id === value,
              );
              return matchedDamage?.name ?? value;
            },
          }}
        >
          {[
            { id: "", value: "No Damage", name: <span>No Damage</span> },
            ...originDamageList,
          ].map((damageOption) => (
            <MenuItem key={damageOption.id} value={damageOption.id}>
              {damageOption.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={6} sm={2}>
        <Button
          variant="contained"
          color="primary"
          className={classes.iconButton}
          onClick={() => doHandleSave()}
          disabled={isSaveButtonDisabled()}
          startIcon={<SaveAltOutlinedIcon />}
        >
          {!selectedInventoryItem?.id ||
          selectedInventoryItem?.status === InventoryStatus.IN_TRANSIT
            ? "place"
            : "save"}
        </Button>
      </Grid>
    </Grid>
  );
};
